// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import { BundleSearch } from '@/bundle/components/bundle-search';
import { NumberTextField } from '@/components/number-text-field';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { SparseItem } from '@/sparse/api/types';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const PREVIEW_PLACEHOLDER = 'Select a bundle to preview its items';
const PREVIEW_HEIGHT_PX = 240;
const QUANTITY_WIDTH_SPACING = 14;

/**
 * One computed line emitted to the calling surface on confirm:
 * the member item at its default quantity scaled by the modal's
 * quantity multiplier, in bundle member order.
 */
export interface BundleApplyLine {
  item: SparseItem;
  quantity: number;
}

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog closes with no changes.
   */
  onClose: () => void;

  /**
   * Callback fired on confirm with the computed batch of line additions,
   * in the order defined on the bundle. The calling surface owns appending
   * the lines under its own duplicate-row semantics and showing the toast.
   */
  onApply: (lines: BundleApplyLine[], bundle: Bundle) => void;
}

/**
 * The apply modal shared by every surface that supports applying a bundle.
 * The user picks a bundle, sets a single quantity, and previews the lines
 * that will append; the preview is strictly read-only. The same component
 * serves page-hosted and dialog-hosted surfaces (standard MUI stacked
 * dialog behavior).
 */
export function ApplyBundleDialog(props: Props) {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [quantity, setQuantity] = useState<number | null>(1);

  const members =
    bundle === null
      ? []
      : [...bundle.members].sort((a, b) => a.position - b.position);

  const isQuantityValid =
    quantity !== null && Number.isInteger(quantity) && quantity >= 1;
  const canApply = bundle !== null && isQuantityValid;

  // Total units the apply will add, for the preview footer; null while the
  // quantity is invalid so the footer degrades to the SKU count alone
  const totalUnits = isQuantityValid
    ? members.reduce((sum, member) => sum + member.defaultQty * quantity, 0)
    : null;

  // Reset synchronously on close so a quick reopen never shows the
  // previous bundle or quantity
  const handleClose = () => {
    props.onClose();
    setBundle(null);
    setQuantity(1);
  };

  const handleApply = () => {
    if (bundle === null || quantity === null) {
      return;
    }
    const lines = members.map((member) => ({
      item: member.item,
      quantity: member.defaultQty * quantity,
    }));
    props.onApply(lines, bundle);
    handleClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={handleClose}>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          Add bundle
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} paddingTop={1}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ flexGrow: 1 }}>
              <BundleSearch
                label="Bundle"
                value={bundle}
                onChange={setBundle}
              />
            </Box>
            <Box
              sx={{ width: (theme) => theme.spacing(QUANTITY_WIDTH_SPACING) }}
            >
              <NumberTextField
                fullWidth
                label="Quantity"
                value={quantity}
                onChange={(values) => {
                  setQuantity(
                    values.value === '' ? null : (values.floatValue ?? null)
                  );
                }}
                allowZero={false}
                size="small"
              />
            </Box>
          </Stack>
          {bundle === null ? (
            <Paper
              variant="outlined"
              sx={{
                height: PREVIEW_HEIGHT_PX,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {PREVIEW_PLACEHOLDER}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ height: PREVIEW_HEIGHT_PX }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Default qty</TableCell>
                      <TableCell align="right">Qty to add</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.item.id}>
                        <TableCell>
                          {member.item.sku}{' '}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                          >
                            ({member.item.name})
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {member.defaultQty}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {isQuantityValid ? member.defaultQty * quantity : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="right"
              >
                {totalUnits === null
                  ? pluralize(members.length, { singular: 'item' })
                  : `${pluralize(members.length, { singular: 'item' })} · ${pluralize(totalUnits, { singular: 'unit' })} will be added`}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canApply} onClick={handleApply}>
          Add items
        </Button>
      </DialogActions>
    </Dialog>
  );
}
